import type { STTJob } from "@/models/stt/base";
import type { VADChunk, VADJob } from "@/models/vad/base";
import { definePlugin } from "@/plugins/server/builder";
import { audioChunkToMs } from "@/shared/audio-chunk-to-ms";
import { lifeError } from "@/shared/error";
import { MessageList } from "@/shared/messages";
import * as op from "@/shared/operation";
import { RollingBuffer } from "@/shared/rolling-buffer";
import { generationPluginConfigDef } from "./config";
import { generationPluginContextDef } from "./context";
import { type GenerationPluginEvent, generationPluginEventsDef } from "./events";
import { GenerationOrchestrator } from "./orchestrator";
import { computeStatus } from "./status";

export const generationPlugin = definePlugin("generation")
  .$config(generationPluginConfigDef)
  .$events(generationPluginEventsDef)
  .$context(generationPluginContextDef)

  // 1. Re-compute status based on events
  .addHandler({
    name: "maintain-status",
    mode: "block",
    onEvent: ({ plugin, event }) => {
      if (!/^agent\.(thinking|speaking)/.test(event.name)) return;
      const status = op.dataOrThrow(computeStatus(plugin.context.get().status, event.name));
      plugin.context.set((ctx) => ({ ...ctx, status }));
    },
  })

  // 2. Update the messages history based on events
  .addHandler({
    name: "maintain-messages",
    mode: "block",
    /**
     * @returns The id of the created or updated message (if any).
     */
    onEvent: ({ event, plugin }) => {
      const messages = new MessageList(plugin.context.get().messages);
      let updatedMessageId: string | undefined;

      // Handle create/update message requests
      if (event.name === "messages.create") {
        updatedMessageId = op.dataOrThrow(messages.create(event.data.message));
      } else if (event.name === "messages.update") {
        updatedMessageId = op.dataOrThrow(
          messages.update(event.data.id, event.data.role, event.data.message),
        );
      }

      // Handle user text chunks: If the user was the last role to speak, append, else create a new user message
      else if (event.name === "user.text-chunk") {
        const message = op.dataOrThrow(messages.findLastFromRoles(["user", "agent", "tool"]));
        if (message?.role === "user") {
          const content = `${message.content}${event.data.textChunk}`;
          updatedMessageId = op.dataOrThrow(messages.update(message.id, "user", { content }));
        } else
          updatedMessageId = op.dataOrThrow(
            messages.create({ role: "user", content: event.data.textChunk }),
          );
      }

      // Handle user interruptions
      else if (event.name === "user.interrupted") {
        const message = op.dataOrThrow(messages.findLastFromRoles(["user"]));
        if (!message) return;
        const content = `${message.content} [You interrupted the user, you might want to quickly apologize and mention that you're caring about what the user said]`;
        updatedMessageId = op.dataOrThrow(messages.update(message.id, "user", { content }));
      }

      // Handle agent tool requests
      else if (event.name === "agent.tool-requests") {
        const message = op.dataOrThrow(messages.findLastFromRoles(["user", "agent"]));
        if (message?.role === "agent") {
          const toolsRequests = [...message.toolsRequests, ...event.data.requests];
          updatedMessageId = op.dataOrThrow(
            messages.update(message.id, "agent", { toolsRequests }),
          );
        } else {
          updatedMessageId = op.dataOrThrow(
            messages.create({ role: "agent", toolsRequests: event.data.requests }),
          );
        }
      }

      // Handle agent interruptions
      else if (event.name === "agent.interrupted") {
        const message = op.dataOrThrow(messages.findLastFromRoles(["agent"]));
        if (!message)
          throw lifeError({
            code: "NotFound",
            message: "No agent message found. Should not happen.",
          });
        if (!message.content.includes("[Interrupted")) {
          const content = `${message.content} [Interrupted by ${event.data.author}]`;
          updatedMessageId = op.dataOrThrow(messages.update(message.id, "agent", { content }));
        }
      }

      // Handle agent text chunks
      else if (event.name === "agent.text-chunk") {
        const message = op.dataOrThrow(messages.findLastFromRoles(["user", "agent", "tool"]));
        if (message?.role === "agent" && !message.content.includes("[Interrupted")) {
          const content = `${message.content}${event.data.textChunk}`;
          updatedMessageId = op.dataOrThrow(messages.update(message.id, "agent", { content }));
        }
      } else if (event.name === "agent.thinking-start") {
        updatedMessageId = op.dataOrThrow(messages.create({ role: "agent" }));
      }

      // Save the modified messages
      const messagesRaw = op.dataOrThrow(messages.getAll());
      plugin.context.set((ctx) => ({ ...ctx, messages: messagesRaw }));
      return updatedMessageId;
    },
  })

  // 3. Consume incoming audio chunks from the WebRTC room
  .addHandler({
    name: "receive-user-audio",
    mode: "block",
    state: { unsubscribe: null as (() => void) | null, count: 9 },
    onEvent: ({ event, plugin, state, agent }) => {
      if (event.name === "plugin.start") {
        state.unsubscribe = agent.transport.on("audio-chunk", (evt) => {
          plugin.events.emit({ name: "user.audio-chunk", data: { audioChunk: evt.chunk } });
        });
      } else if (event.name === "plugin.stop") state?.unsubscribe?.();
    },
  })

  // 4. Use VAD model to detect user voice activity in incoming audio chunks
  .addHandler({
    name: "detect-user-voice",
    mode: "stream",
    state: ({ config }) => ({
      inSpeech: false,
      prePaddingBuffer: new RollingBuffer<Int16Array>(config.voiceDetection.prePaddingChunks),
      postPaddingCountdown: 0,
      // Keeps track of 3x the min. interruption duration in audio chunks
      interruptBuffer: new RollingBuffer<Int16Array>(
        (config.voiceDetection.minVoiceInterruptionMs / 10) * 3,
      ),
      voiceWindow: [] as Array<{ timestamp: number; duration: number }>,
      vadJob: null as VADJob | null,
    }),
    onEvent: async ({ event, state, agent, plugin }) => {
      // Helper to process VAD model results
      const processVADResults = (chunk: VADChunk) => {
        // Helper method to emit a voice chunk
        const emitVoiceChunk = (
          data: Extract<GenerationPluginEvent<"output">, { name: "user.voice-chunk" }>["data"],
        ) => plugin.events.emit({ name: "user.voice-chunk", data });
        // Helper to calculate current interrupt duration from sliding window
        const getCurrentInterruptDuration = () => {
          const now = Date.now();
          // Remove chunks older than the voice window
          const voiceWindowMs = plugin.config.voiceDetection.minVoiceInterruptionMs * 2; // twice the min. interruption duration
          state.voiceWindow = state.voiceWindow.filter((c) => now - c.timestamp <= voiceWindowMs);
          // Sum up durations of remaining chunks
          return state.voiceWindow.reduce((sum, chk) => sum + chk.duration, 0);
        };
        // Check if the chunk contains voice
        const inSpeechBefore = state.inSpeech;
        state.inSpeech =
          chunk.score >
          (state.inSpeech
            ? plugin.config.voiceDetection.scoreInThreshold
            : plugin.config.voiceDetection.scoreOutThreshold);
        const inSpeechChanged = state.inSpeech !== inSpeechBefore;
        // If the agent is currently listening
        if (plugin.context.get().status.listening) {
          // If the current chunk contains voice
          if (state.inSpeech) {
            // Reset post-padding count for the new voice session
            state.postPaddingCountdown = plugin.config.voiceDetection.postPaddingChunks;
            // Emit the voice start event
            if (inSpeechChanged && state.prePaddingBuffer.length() > 0)
              plugin.events.emit({ name: "user.voice-start" });
            // Emit the pre-padding chunks
            for (let i = state.prePaddingBuffer.length() - 1; i >= 0; i--) {
              const voiceChunk = state.prePaddingBuffer.get()[i];
              if (!voiceChunk) continue;
              emitVoiceChunk({
                type: "padding",
                voiceChunk,
                paddingSide: "pre",
                paddingIndex: i,
              });
            }
            state.prePaddingBuffer.empty();
            // Emit the voice chunk
            emitVoiceChunk({ type: "voice", voiceChunk: chunk.chunk });
          }
          // Else, emit the current chunk if post-padding limit is not met yet
          else if (state.postPaddingCountdown > 0) {
            emitVoiceChunk({
              type: "padding",
              voiceChunk: chunk.chunk,
              paddingSide: "post",
              paddingIndex: state.postPaddingCountdown,
            });
            state.postPaddingCountdown--;
            if (state.postPaddingCountdown === 0) plugin.events.emit({ name: "user.voice-end" });
          }
          // Else, add the current chunk to the pre-padding buffer
          else state.prePaddingBuffer.add(chunk.chunk);
        }
        // Or if the agent wasn't listening already
        else {
          // Buffer the audio chunk
          state.interruptBuffer.add(chunk.chunk);
          // If voice is detected, add to sliding window
          if (state.inSpeech) {
            const duration = audioChunkToMs(chunk.chunk);
            state.voiceWindow.push({
              timestamp: Date.now(),
              duration,
            });
          }
          // If the interruption duration is long enough, abort and emit all accumulated voice chunks
          if (
            getCurrentInterruptDuration() >= plugin.config.voiceDetection.minVoiceInterruptionMs
          ) {
            plugin.events.emit({
              name: "agent.interrupt",
              data: { reason: "The user is speaking", author: "user" },
              urgent: true,
            });
            plugin.events.emit({ name: "user.voice-start" });
            for (const voiceChunk of state.interruptBuffer.get()) {
              emitVoiceChunk({ type: "voice", voiceChunk });
            }
            state.interruptBuffer.empty();
            state.voiceWindow.length = 0; // Clear the sliding window
          }
        }
      };

      // Create the VAD job on plugin start, and consume results
      if (event.name === "plugin.start") {
        state.vadJob = await agent.models.vad.detect();
        (async () => {
          for await (const chunk of state.vadJob?.stream ?? []) {
            processVADResults(chunk);
          }
        })();
      }
      // Clean up VAD job on plugin stop
      else if (event.name === "plugin.stop") state?.vadJob?.cancel();
      // Push audio chunk to VAD model
      else if (event.name === "user.audio-chunk") {
        state.vadJob?.inputVoice(event.data.audioChunk);
      }
    },
  })

  // 5. Use STT model to transcribe user voice chunks
  .addHandler({
    name: "transcribe-user-voice",
    mode: "stream",
    state: { sttJob: null as STTJob | null },
    onEvent: async ({ event, plugin, state, agent }) => {
      // Create the STT job on plugin start, and consume results
      if (event.name === "plugin.start") {
        state.sttJob = await agent.models.stt.generate();
        (async () => {
          for await (const chunk of state.sttJob?.stream ?? []) {
            if (chunk.type === "content") {
              plugin.events.emit({ name: "user.text-chunk", data: { textChunk: chunk.textChunk } });
            }
          }
        })();
      }
      // Clean up STT job on plugin stop
      else if (event.name === "plugin.stop") state?.sttJob?.cancel();

      // Stream voice chunks to the STT model
      if (event.name !== "user.voice-chunk") return;
      state.sttJob?.inputVoice(event.data.voiceChunk);
    },
  })

  // 6. Use EOU model to detect user's end of turn
  .addHandler({
    name: "detect-end-of-turn",
    mode: "stream",
    state: {
      userIsSpeaking: false,
      timeoutId: null as NodeJS.Timeout | null,
      lastMessageBuffer: "",
    },
    onEvent: async ({ event, plugin, state, agent }) => {
      // Helper method to check if the agent can answer
      const canAnswer = () => {
        if (state.userIsSpeaking) return false;
        if (!state.lastMessageBuffer.trim().length) return false;
        return true;
      };

      // Helper method to emit the current message buffer
      const answer = () => {
        if (state.timeoutId) clearTimeout(state.timeoutId);
        if (!canAnswer()) return;
        plugin.events.emit({ name: "agent.continue", data: { interrupt: "abrupt" }, urgent: true });
        state.lastMessageBuffer = "";
      };

      // If the agent is not listening, return
      if (!plugin.context.get().status.listening) return;

      // Handle voice related events and text chunks
      if (event.name === "user.voice-start") state.userIsSpeaking = true;
      else if (event.name === "user.voice-end") state.userIsSpeaking = false;
      else if (event.name === "user.text-chunk") state.lastMessageBuffer += event.data.textChunk;
      else return;

      // Clear the timeout if it exists
      if (state.timeoutId) clearTimeout(state.timeoutId);

      // If the agent can't answer yet, continue
      if (!canAnswer()) return;

      // Determine if the user has finished speaking
      const endOfTurnProbability = await agent.models.eou.predict(plugin.context.get().messages);

      // Emit the message if the user has finished speaking
      if (endOfTurnProbability >= plugin.config.endOfTurnDetection.threshold) answer();
      // Else, set a timeout to emit the message after a delay
      else {
        state.timeoutId = setTimeout(
          answer,
          Math.max(
            plugin.config.endOfTurnDetection.minTimeoutMs,
            plugin.config.endOfTurnDetection.maxTimeoutMs *
              (1 - endOfTurnProbability / plugin.config.endOfTurnDetection.threshold),
          ),
        );
      }
    },
  })

  // 7. Generate agent response generation operations
  .addHandler({
    name: "generate-agent-response",
    mode: "block",
    state: { orchestrator: null as null | GenerationOrchestrator },
    onEvent: ({ event, plugin, state, agent }) => {
      if (event.name === "plugin.start") {
        // @ts-expect-error - here not all handlers are available yet in the type, but it's fine
        state.orchestrator = new GenerationOrchestrator({ plugin, agent });
        state.orchestrator.start();
      }
      state?.orchestrator?.pushEvent(event);
    },
  })

  // 8. Handle resources requests
  .addHandler({
    name: "handle-resources-requests",
    mode: "block",
    onEvent: ({ event, plugin }) => {
      if (event.name !== "agent.resources-request") return;
      plugin.events.emit({
        name: "agent.resources-response",
        data: {
          requestId: event.id,
          resources: { messages: plugin.context.get().messages, tools: [] },
        },
      });
    },
  })

  // 9. Handle tools requests
  .addHandler({
    name: "handle-tools-requests",
    mode: "block",
    onEvent: ({ event, plugin }) => {
      if (event.name !== "agent.tool-requests") return;
      for (const request of event.data.requests) {
        plugin.events.emit({
          name: "messages.create",
          data: {
            message: {
              role: "tool",
              toolRequestId: request.toolRequestId,
              toolName: request.toolName,
              toolSuccess: false,
              toolError: "Tool calling is not implemented.",
            },
          },
        });
      }
    },
  })

  // 10. Stream agent speech to the user
  .addHandler({
    name: "stream-outgoing-audio",
    mode: "stream",
    onEvent: ({ event, agent }) => {
      if (event.name !== "agent.voice-chunk") return;
      agent.transport.streamAudioChunk(event.data.voiceChunk);
    },
  });
